import { format, isValid, parse, parseISO } from "date-fns";
import Highcharts from "highcharts";

import { LINE_CHART_CONFIG } from "src/config/charts/lineChartConfig";
import { COLOR_LIST } from "src/constants/dashboard";

type NormalizedCategory = {
  key: string;
  label: string;
};

const QUARTER_PATTERN = /^Q([1-4])-(\d{4})$/i;
const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_KEY_PATTERN = /^\d{4}-\d{2}$/;

const getCategorySortValue = (key: string): number | null => {
  if (DAY_KEY_PATTERN.test(key)) {
    const parsedDay = parse(key, "yyyy-MM-dd", new Date());
    return isValid(parsedDay) ? parsedDay.getTime() : null;
  }

  if (MONTH_KEY_PATTERN.test(key)) {
    const parsedMonth = parse(key, "yyyy-MM", new Date());
    return isValid(parsedMonth) ? parsedMonth.getTime() : null;
  }

  const quarterMatch = key.match(QUARTER_PATTERN);
  if (quarterMatch) {
    const quarter = Number(quarterMatch[1]);
    const year = Number(quarterMatch[2]);
    if (!Number.isFinite(quarter) || !Number.isFinite(year)) {
      return null;
    }

    const quarterStartMonth = (quarter - 1) * 3;
    return new Date(year, quarterStartMonth, 1).getTime();
  }

  return null;
};

const sortNormalizedCategories = (
  categories: NormalizedCategory[],
): NormalizedCategory[] =>
  categories.slice().sort((left, right) => {
    const leftSortValue = getCategorySortValue(left.key);
    const rightSortValue = getCategorySortValue(right.key);

    if (leftSortValue !== null && rightSortValue !== null) {
      return leftSortValue - rightSortValue;
    }

    if (leftSortValue !== null) {
      return -1;
    }

    if (rightSortValue !== null) {
      return 1;
    }

    return left.key.localeCompare(right.key);
  });

const normalizeXAxisValue = (value: unknown): NormalizedCategory | null => {
  if (value instanceof Date && isValid(value)) {
    const key = format(value, "yyyy-MM-dd");
    return { key, label: format(value, "dd. MMM") };
  }

  const raw = String(value ?? "").trim();
  if (!raw) return null;

  const quarterMatch = raw.match(QUARTER_PATTERN);
  if (quarterMatch) {
    return { key: raw.toUpperCase(), label: raw.toUpperCase() };
  }

  const parsedDate = parse(raw, "yyyy-MM-dd", new Date());
  if (isValid(parsedDate)) {
    const key = format(parsedDate, "yyyy-MM-dd");
    return { key, label: format(parsedDate, "dd. MMM") };
  }

  const parsedMonth = parse(raw, "yyyy-MM", new Date());
  if (isValid(parsedMonth)) {
    const key = format(parsedMonth, "yyyy-MM");
    return { key, label: format(parsedMonth, "MMM yyyy") };
  }

  const parsedIso = parseISO(raw);
  if (isValid(parsedIso)) {
    const key = format(parsedIso, "yyyy-MM-dd");
    return { key, label: format(parsedIso, "dd. MMM") };
  }

  const parsedNative = new Date(raw);
  if (isValid(parsedNative)) {
    const key = format(parsedNative, "yyyy-MM-dd");
    return { key, label: format(parsedNative, "dd. MMM") };
  }

  return { key: raw, label: raw };
};

const populateLineBase = (data: any): NormalizedCategory[] => {
  const categoryMap = new Map<string, NormalizedCategory>();

  data.forEach((caseVolume: any) => {
    (caseVolume?.data ?? []).forEach((item: { x: unknown }) => {
      const normalized = normalizeXAxisValue(item?.x);
      if (!normalized) return;
      if (!categoryMap.has(normalized.key)) {
        categoryMap.set(normalized.key, normalized);
      }
    });
  });

  return sortNormalizedCategories(Array.from(categoryMap.values()));
};

// Map data to the line series, filling missing dates with null
const wrapperFunctionForLine = (data: any) => {
  let lineSeriesArray: any = [];
  const xAxisCategories = populateLineBase(data);

  data.forEach((caseVolume: any, index: number) => {
    const seriesData: Array<number | null> = [];

    const valuesByDate = new Map<string, number>();
    (caseVolume?.data ?? []).forEach((item: { x: unknown; y: number }) => {
      const normalized = normalizeXAxisValue(item?.x);
      if (!normalized) return;
      valuesByDate.set(normalized.key, item.y);
    });

    xAxisCategories.forEach((category) => {
      seriesData.push(valuesByDate.get(category.key) ?? null);
    });

    lineSeriesArray.push({
      type: "spline",
      name: caseVolume.name,
      data: seriesData,
      color: COLOR_LIST[index],
      boostThreshold: 0,
    });
  });

  return lineSeriesArray;
};

export const renderLine = (
  data: unknown,
  spec: unknown,
  title: string,
): Highcharts.Options => {
  const safeData = Array.isArray(data) ? data : [];
  const xAxisCategories = populateLineBase(safeData);

  return {
    ...LINE_CHART_CONFIG,
    series: wrapperFunctionForLine(safeData),
    xAxis: {
      categories: xAxisCategories.map((item) => item.label),
    },
    // title: {
    //   text: title,
    //   align: "left",
    // },
  };
};
